import React, { useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Brain, Zap, Target, AlertCircle, TrendingUp, TrendingDown, Activity, Download, DollarSign, Lock, Shield, Info, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { getCurrencySymbol } from '../countryConfig';
import { formatChartValue } from '../utils/chartFormatters';

const EnhancedAIAnalysis = ({
  aiAnalysis,
  analyzing,
  runAIAnalysis,
  userProfile,
  onUpgrade,
  selectedCountry
}) => {
  const currencySymbol = getCurrencySymbol(selectedCountry);
  const reportRef = useRef(null);

  if (!aiAnalysis && !analyzing) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">AI-Powered Portfolio Analysis</h3>
            <p className="text-sm text-gray-600 mt-1">Get comprehensive insights across all your investments</p>
          </div>
          <button
            onClick={runAIAnalysis}
            disabled={analyzing}
            className="px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
          >
            <Brain className="w-5 h-5" />
            <span>Run AI Analysis</span>
          </button>
        </div>
        <div className="text-center py-12 text-gray-500">
          <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>Click "Run AI Analysis" to get personalized insights</p>
          <p className="text-sm mt-2">Analyzes investments, crypto, accounts, and more</p>
        </div>
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Analyzing your portfolio...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  const handleExportPDF = async () => {
    // Dynamic import for PDF export libraries
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('PlanLens-AI-Analysis-Report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF export requires additional libraries. Please install html2canvas and jspdf: npm install html2canvas jspdf');
    }
  };

  // Scenario chart data
  const scenarioData = [
    {
      name: 'Best Case',
      value: aiAnalysis.scenarios?.best || 0,
      fill: '#10b981'
    },
    {
      name: 'Median',
      value: aiAnalysis.scenarios?.median || 0,
      fill: '#3b82f6'
    },
    {
      name: 'Worst Case',
      value: aiAnalysis.scenarios?.worst || 0,
      fill: '#ef4444'
    }
  ];

  // Portfolio breakdown data - use assetBreakdown if available, fallback to old format
  const portfolioBreakdownData = aiAnalysis.assetBreakdown
    ? Object.entries(aiAnalysis.assetBreakdown).map(([name, value], index) => {
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6'];
        return {
          name,
          value,
          color: colors[index % colors.length]
        };
      }).filter(item => item.value > 0)
    : [
        { name: 'Investments', value: aiAnalysis.investmentsValue || 0, color: '#3b82f6' },
        { name: 'Crypto', value: aiAnalysis.cryptoValue || 0, color: '#f59e0b' },
        { name: 'Accounts', value: aiAnalysis.accountsValue || 0, color: '#10b981' }
      ].filter(item => item.value > 0);

  const getCommentaryIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'positive': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'info': return <Info className="w-5 h-5 text-blue-600" />;
      case 'suggestion': return <Zap className="w-5 h-5 text-purple-600" />;
      default: return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const getCommentaryStyle = (type) => {
    switch (type) {
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      case 'positive': return 'border-green-500 bg-green-50';
      case 'info': return 'border-blue-500 bg-blue-50';
      case 'suggestion': return 'border-purple-500 bg-purple-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">AI-Powered Portfolio Analysis</h3>
            <p className="text-sm text-gray-600 mt-1">Comprehensive analysis of all your holdings</p>
            {aiAnalysis?.timestamp && (
              <p className="text-xs text-gray-500 mt-1">
                Generated {new Date(aiAnalysis.timestamp).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Report (PDF)</span>
            </button>
            <button
              onClick={runAIAnalysis}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              title="Generate fresh analysis with latest data"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Regenerate
            </button>
          </div>
        </div>

        {/* Educational Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Educational Tool Only</p>
              <p>This analysis is for educational purposes only and does not constitute financial advice. Always consult with a qualified Independent Financial Advisor (IFA) for personalized financial guidance.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Report Content */}
      <div ref={reportRef} className="space-y-6">
        {/* Portfolio Summary */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span>Portfolio Summary</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
              <p className="text-sm text-blue-700 mb-1">Total Value</p>
              <p className="text-2xl font-bold text-blue-900">{currencySymbol}{aiAnalysis.totalValue?.toLocaleString() || '0'}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
              <p className="text-sm text-green-700 mb-1">Expected Return</p>
              <p className="text-2xl font-bold text-green-900">{aiAnalysis.expectedReturn?.toFixed(1) || '0'}%</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
              <p className="text-sm text-yellow-700 mb-1">Volatility</p>
              <p className="text-2xl font-bold text-yellow-900">{aiAnalysis.volatility?.toFixed(1) || '0'}%</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
              <p className="text-sm text-purple-700 mb-1">Sharpe Ratio</p>
              <p className="text-2xl font-bold text-purple-900">{aiAnalysis.sharpeRatio?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>

        {/* Portfolio Breakdown */}
        {portfolioBreakdownData.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-6">Portfolio Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={portfolioBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                      outerRadius={130}
                      innerRadius={0}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={2}
                      isAnimationActive={false}
                    >
                      {portfolioBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `${currencySymbol}${value.toLocaleString()}`}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {portfolioBreakdownData.map((item, index) => {
                  const total = portfolioBreakdownData.reduce((sum, entry) => sum + entry.value, 0);
                  const percentage = ((item.value / total) * 100).toFixed(1);
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                        <span className="font-medium text-gray-900">{item.name}</span>
                        <span className="text-sm text-gray-600">({percentage}%)</span>
                      </div>
                      <span className="font-bold text-gray-900">{currencySymbol}{item.value.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Risk & Diversification Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Risk Assessment */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Shield className="w-5 h-5 text-red-600" />
              <span>Risk Level</span>
            </h4>
            <div className="flex flex-col items-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold ${
                aiAnalysis.riskScore === 'High' ? 'bg-red-100 text-red-700' :
                aiAnalysis.riskScore === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {aiAnalysis.riskScore?.charAt(0) || 'L'}
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-4">{aiAnalysis.riskScore || 'Low'}</p>
              <p className="text-sm text-gray-600 mt-1">Risk Level</p>
            </div>
          </div>

          {/* Diversification Score */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Activity className="w-5 h-5 text-green-600" />
              <span>Diversification</span>
            </h4>
            <div className="flex flex-col items-center">
              <div className="relative w-24 h-24">
                <svg className="transform -rotate-90 w-24 h-24">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#10b981"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(aiAnalysis.diversificationScore || 0) * 2.51} 251`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900">{aiAnalysis.diversificationScore || 0}</span>
                </div>
              </div>
              <p className="text-2xl font-bold text-green-600 mt-4">{aiAnalysis.diversificationRating || 'Good'}</p>
              <p className="text-sm text-gray-600 mt-1">Diversification</p>
            </div>
          </div>

          {/* Sharpe Ratio Gauge */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span>Risk-Adjusted Return</span>
            </h4>
            <div className="flex flex-col items-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold ${
                (aiAnalysis.sharpeRatio || 0) > 1 ? 'bg-green-100 text-green-700' :
                (aiAnalysis.sharpeRatio || 0) > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {aiAnalysis.sharpeRatio?.toFixed(1) || '0.0'}
              </div>
              <p className="text-xl font-bold text-gray-900 mt-4">Sharpe Ratio</p>
              <p className="text-sm text-gray-600 mt-1 text-center">
                {(aiAnalysis.sharpeRatio || 0) > 1 ? 'Excellent' :
                 (aiAnalysis.sharpeRatio || 0) > 0.5 ? 'Good' : 'Needs Improvement'}
              </p>
            </div>
          </div>
        </div>

        {/* Scenario Projections */}
        {aiAnalysis.scenarios && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <Target className="w-5 h-5 text-blue-600" />
              <span>Scenario Projections ({aiAnalysis.scenarios.years} Years)</span>
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scenarioData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  stroke="#9ca3af"
                  label={{ value: 'Scenario', position: 'insideBottom', offset: -20 }}
                />
                <YAxis
                  stroke="#9ca3af"
                  tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                  label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft' }}
                  width={100}
                />
                <Tooltip formatter={(value) => `${currencySymbol}${value.toLocaleString()}`} />
                <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
                  {scenarioData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700 mb-1 font-semibold">Best Case</p>
                <p className="text-xl font-bold text-green-900">{currencySymbol}{aiAnalysis.scenarios.best?.toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">Optimistic scenario</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 mb-1 font-semibold">Median Case</p>
                <p className="text-xl font-bold text-blue-900">{currencySymbol}{aiAnalysis.scenarios.median?.toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-1">Expected scenario</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700 mb-1 font-semibold">Worst Case</p>
                <p className="text-xl font-bold text-red-900">{currencySymbol}{aiAnalysis.scenarios.worst?.toLocaleString()}</p>
                <p className="text-xs text-red-600 mt-1">Conservative scenario</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Commentary */}
        {aiAnalysis.commentary && aiAnalysis.commentary.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <Brain className="w-5 h-5 text-blue-600" />
              <span>AI Insights & Commentary</span>
            </h4>
            <div className="space-y-3">
              {aiAnalysis.commentary.map((comment, index) => (
                <div key={index} className={`border-l-4 rounded-r-lg p-4 ${getCommentaryStyle(comment.type)}`}>
                  <div className="flex items-start space-x-3">
                    {getCommentaryIcon(comment.type)}
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">{comment.title}</h5>
                      <p className="text-sm text-gray-700">{comment.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Recommendations Educational */}
        {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span>AI Recommendations</span>
            </h4>
            <div className="space-y-4">
              {aiAnalysis.recommendations.map((rec, index) => (
                <div key={index} className={`border-l-4 rounded-r-lg p-4 ${
                  rec.priority === 'high' ? 'border-red-500 bg-red-50' :
                  rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                  'border-green-500 bg-green-50'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        rec.priority === 'high' ? 'bg-red-200 text-red-800' :
                        rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-green-200 text-green-800'
                      }`}>
                        {rec.priority?.toUpperCase() || 'MEDIUM'} PRIORITY
                      </span>
                      {rec.countrySpecific && (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-200 text-blue-800">
                          {selectedCountry === 'US' ? '🇺🇸 US' : '🇬🇧 UK'} SPECIFIC
                        </span>
                      )}
                    </div>
                  </div>
                  <h5 className="font-semibold text-gray-900 mb-2">{rec.title}</h5>
                  <p className="text-gray-700 mb-3">{rec.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-green-600">
                      💰 Potential Impact: {rec.impact}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedAIAnalysis;
